import Phaser from 'phaser'
import { IController, RingBuffer, ActionEvent, GameAction } from './IController'
import Toast from '../../ui/Toast'
import GamepadStatus from '../../ui/GamepadStatus'

type RepeatableDirection = 'left' | 'right' | 'down'

interface RepeatState {
held: boolean
elapsed: number
fired: boolean
}

const BUTTON_MAP: ReadonlyMap<number, GameAction> = new Map([
[0, 'rotateClockwise'], // A / Cross
[1, 'rotateCounterClockwise'], // B / Circle
[2, 'hardDrop'], // X / Square
[3, 'purify'], // Y / Triangle
[9, 'pause'], // Start
])

const DPAD = { UP: 12, DOWN: 13, LEFT: 14, RIGHT: 15 } as const

export class GamepadController implements IController {
public readonly schemeName = 'Gamepad'
public readonly actions: RingBuffer<ActionEvent>

private readonly REPEAT_INITIAL_DELAY = 200
private readonly REPEAT_RATE = 100

private enabled: boolean = false
private debugMode: boolean = false

// UI hooks
private toast?: Toast
private status?: GamepadStatus
private lastConnected: boolean = false

// Core input state
private prevButtonStates: Map<number, boolean> = new Map()
private repeatStates: Map<RepeatableDirection, RepeatState> = new Map([
['left', { held: false, elapsed: 0, fired: false }],
['right', { held: false, elapsed: 0, fired: false }],
['down', { held: false, elapsed: 0, fired: false }],
])
private scene: Phaser.Scene

// Optional: existing counters used elsewhere in your original file
private showLock: boolean = false

constructor(scene: Phaser.Scene) {
this.scene = scene
this.actions = new RingBuffer<ActionEvent>(64)

// UI hooks – only create when scene has Phaser's add factory
if (scene?.add) {
this.toast = new Toast(scene)
this.status = new GamepadStatus(scene)
}
}

// Convenience: Enable the Phaser gamepad input if available
onEnable(): void {
if (this.enabled) return
this.enabled = true

if (this.scene?.input?.gamepad) {
this.scene.input.gamepad.enabled = true
}
}

onDisable(): void {
this.enabled = false
this.prevButtonStates.clear()
this.resetAllRepeatStates()
}

/**
* Update called every frame with delta time in ms
*/
update(dtMs: number): void {
if (!this.enabled) return

const pad = this.getConnectedPad()
// Connection notification (edge-triggered)
const isConnected = pad?.connected ?? false
if (isConnected !== this.lastConnected) {
this.notifyConnectionStatus(pad ?? null)
}
this.lastConnected = isConnected
if (!pad) return

this.processButtons(pad)
this.processDirectionalInput(pad, dtMs)
}

// --- Helpers (core input) ------------------------------------------------

private getConnectedPad(): Phaser.Input.Gamepad.Gamepad | null {
if (!this.scene?.input?.gamepad) return null

const pads = this.scene.input.gamepad.gamepads
if (!pads || pads.length === 0) return null

for (const pad of pads) {
if (pad && pad.connected) return pad
}
return null
}

// Non-directional mapped buttons (edge-triggered)
private processButtons(pad: Phaser.Input.Gamepad.Gamepad): void {
for (const [index, action] of BUTTON_MAP) {
const button = pad.buttons[index]
if (!button) continue

const pressed = button.pressed
const wasPressed = this.prevButtonStates.get(index) ?? false

if (pressed && !wasPressed) {
this.pushAction(action)
}

this.prevButtonStates.set(index, pressed)
}
}

// Directional input with DAS-like repeat behavior
private processDirectionalInput(
pad: Phaser.Input.Gamepad.Gamepad,
dtMs: number
): void {
const dpadLeft = pad.buttons[DPAD.LEFT]?.pressed ?? false
const dpadRight = pad.buttons[DPAD.RIGHT]?.pressed ?? false
const dpadDown = pad.buttons[DPAD.DOWN]?.pressed ?? false

const stickX = pad.axes.length > 0 ? pad.axes[0].getValue() : 0
const stickY = pad.axes.length > 1 ? pad.axes[1].getValue() : 0

const DEAD_ZONE = 0.3
const stickLeft = stickX < -DEAD_ZONE
const stickRight = stickX > DEAD_ZONE
const stickDown = stickY > DEAD_ZONE

const leftHeld = dpadLeft || stickLeft
const rightHeld = dpadRight || stickRight
const downHeld = dpadDown || stickDown

this.updateDirectionalRepeat('left', leftHeld, 'moveLeft', dtMs)
this.updateDirectionalRepeat('right', rightHeld, 'moveRight', dtMs)
this.updateDirectionalRepeat('down', downHeld, 'softDrop', dtMs)
}

private updateDirectionalRepeat(
direction: RepeatableDirection,
held: boolean,
action: GameAction,
dtMs: number
): void {
const state = this.repeatStates.get(direction)!

if (held) {
if (!state.held) {
// Just pressed
this.pushAction(action)
state.held = true
state.elapsed = 0
state.fired = false
} else {
state.elapsed += dtMs
const threshold = state.fired ? this.REPEAT_RATE ?? 100 : this.REPEAT_INITIAL_DELAY ?? 200
if (state.elapsed >= threshold) {
this.pushAction(action)
state.elapsed -= threshold
state.fired = true
}
}
} else {
if (state.held) {
state.held = false
state.elapsed = 0
state.fired = false
}
}
}

private pushAction(action: GameAction): void {
this.actions.push({ action, timestamp: Date.now() })
}

private resetAllRepeatStates(): void {
for (const state of this.repeatStates.values()) {
state.held = false
state.elapsed = 0
state.fired = false
}
}

// --- UI integration helpers ----------------------------------------------

private notifyConnectionStatus(pad: Phaser.Input.Gamepad.Gamepad | null) {
const connected = !!pad?.connected
this.status?.setConnected(connected)
if (this.toast) {
this.toast.show(connected ? 'Gamepad connected' : 'Gamepad disconnected')
}
}

// --- IController interface (stubs retained) --------------------------------

setHapticEnabled(_enabled: boolean): void {
// Optional: implement if haptics are wired in
}

setDebugMode(enabled: boolean): void {
this.debugMode = enabled
}

isDebugMode(): boolean {
return this.debugMode
}

toggleDebugMode(): boolean {
this.debugMode = !this.debugMode
return this.debugMode
}

resize(_width: number, _height: number): void {
// Optional: respond to resize if needed
}

destroy(): void {
this.onDisable()
this.actions.clear()
}

// Existing internal helpers used by your original file
// If your real file has additional methods, keep them here or merge as needed
}
